import axios from 'axios';
import { z } from 'zod';

import Config from 'Config';

const ActivateUserResponse = z.object({
    stateToken: z.string().optional(),
    sessionToken: z.string().optional(),
});

const SetPasswordResponse = z.object({
    sessionToken: z.string(),
});

type ActivateUserResponse = z.infer<typeof ActivateUserResponse>;
type SetPasswordResponse = z.infer<typeof SetPasswordResponse>;
export type ErrorResponse = {
    errorMessage: string;
};

export const isError = (
    value: ActivateUserResponse | ErrorResponse | { sessionToken: string }
): value is ErrorResponse => 'errorMessage' in value;

export const activateUser = async (activationToken: string): Promise<ActivateUserResponse | ErrorResponse> => {
    try {
        const response = await axios({
            url: `${Config.oktaExternalServerHostUrl}/api/v1/authn`,
            method: 'POST',
            data: {
                token: activationToken,
            },
        });

        const activateUserResponse = ActivateUserResponse.safeParse(response.data);

        if (!activateUserResponse.success) {
            return {
                errorMessage: 'failed to parse activate user response',
            };
        }

        return {
            stateToken: activateUserResponse.data.stateToken,
            sessionToken: activateUserResponse.data.sessionToken,
        };
    } catch {
        return {
            errorMessage: 'activate user request encountered an error',
        };
    }
};

type SetPasswordInput = {
    stateToken: string | undefined;
    password: string;
};

export const setPassword = async ({
    stateToken,
    password,
}: SetPasswordInput): Promise<SetPasswordResponse | ErrorResponse> => {
    try {
        const response = await axios({
            url: `${Config.oktaExternalServerHostUrl}/api/v1/authn/credentials/reset_password`,
            method: 'POST',
            data: {
                stateToken,
                newPassword: password,
            },
        });

        const setPasswordResponse = SetPasswordResponse.safeParse(response.data);

        if (!setPasswordResponse.success) {
            return {
                errorMessage: 'failed to parse set password response',
            };
        }

        return {
            sessionToken: setPasswordResponse.data.sessionToken,
        };
    } catch {
        return {
            errorMessage: 'set password request encountered an error',
        };
    }
};
